import React, { useCallback, useMemo, useState, useEffect } from "react";
// import DatabricksIcon from "../../../assests/databricks.png"
// import AzureDatabricksIcon from "../../../assests/azure-databricks.png"
import AdlsIcon from "../../../assests/ADLS-SVG.svg";
import DatabricksSVG from "../../../assests/Databricks.svg";
import AzureDatabricksSVG from "../../../assests/Azure.svg";
// import dummyData from "./DummyData";
import loader from "../../../assests/Loader5.gif";
import {
  ReactFlow,
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  Handle,
  Position,
  applyEdgeChanges,
  applyNodeChanges,
  BackgroundVariant,
  ReactFlowProvider
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import "./ViewLineage.css";

const mapApiResponseToLineageGraph = (apiResponse) => {
  const nodes = [];
  const edges = [];
  const nodeIdMap = new Map(); // To track node IDs and avoid duplicates
  let nodeCounter = 1;

  // Helper function to generate unique node ID
  const generateNodeId = (tableName) => {
    if (nodeIdMap.has(tableName)) {
      return nodeIdMap.get(tableName);
    }
    const id = nodeCounter.toString();
    nodeIdMap.set(tableName, id);
    nodeCounter++;
    return id;
  };

  // Helper function to extract columns data for TableNode
  const extractColumnsData = (columns) => {
    // Handle null, undefined, non-arrays, or empty arrays
    if (!columns || !Array.isArray(columns) || columns.length === 0) {
      return { visibleColumns: [], allColumns: [], showMoreText: null };
    }

    // Handle both old format (objects with name and type) and new format (just strings)
    const allColumns = columns.map((col) => {
      if (typeof col === 'string') {
        // New format: columns are just strings
        return {
          name: col,
          type: "string", // Default type since API doesn't provide it
        };
      } else {
        // Old format: columns are objects with name and type
        return {
      name: col.name || "",
      type: col.type_name || col.type_text || "unknown",
        };
      }
    });

    // Show first 6 columns by default, rest in "show more"
    const visibleColumns = allColumns.slice(0, 3);
    const remainingCount = allColumns.length - 3;
    const showMoreText =
      remainingCount > 0 ? `Show ${remainingCount} more columns` : null;

    return { visibleColumns, allColumns, showMoreText };
  };

  // Helper function to create a node from metadata
  const createNodeFromMetadata = (
    metadata,
    tableName,
    position = { x: 0, y: 0 },
    isPrimaryNode = false,
    columns = null // New parameter for columns array
  ) => {
    const nodeId = generateNodeId(tableName);
    
    // Use columns parameter first, then fall back to metadata.columns
    const columnsToUse = columns || (metadata && metadata.columns) || [];
    
    const { visibleColumns, allColumns, showMoreText } = extractColumnsData(columnsToUse);
    
    // If no metadata and no columns, provide default information
    if (!metadata && (!columns || columns.length === 0)) {
    return {
      id: nodeId,
      type: "tableNode",
      position,
      data: {
        tableName: tableName,
          owner: "Unknown",
          visibleColumns: [],
          allColumns: [],
          showMoreText: null,
          isPrimaryNode: isPrimaryNode,
        },
      };
    }

    return {
      id: nodeId,
      type: "tableNode",
      position,
      data: {
        tableName: tableName,
        owner: (metadata && metadata.owner) || "Unknown",
        visibleColumns,
        allColumns,
        showMoreText,
        isPrimaryNode: isPrimaryNode,
      },
    };
  };

  // Helper function to process upstreams recursively
  const processUpstreams = (upstreams, parentNodeId, level = 0) => {
    if (!upstreams || !Array.isArray(upstreams)) return;

    // Get parent node to calculate proper positioning
    const parentNode = nodes.find(n => n.id === parentNodeId);
    const parentY = parentNode ? parentNode.position.y : 700;
    
    // Calculate spacing based on number of upstreams
    const totalUpstreams = upstreams.length;
    const spacing = totalUpstreams === 1 ? 0 : 250; // No spacing for single child, 250px for multiple
    const startY = totalUpstreams === 1 ? parentY : parentY - (totalUpstreams - 1) * spacing / 2;

    upstreams.forEach((upstream, index) => {
      const childTableName = upstream.child;
      
      // Skip if no table name is available
      if (!childTableName) return;
      
      const nodeId = generateNodeId(childTableName);

      // Create node if it doesn't exist (regardless of metadata)
      if (!nodes.find((n) => n.id === nodeId)) {
        const position = {
          x: Math.max(0, 500 - (level + 1) * 600), // Increased horizontal spacing
          y: startY + index * spacing,
        };
        const node = createNodeFromMetadata(
          upstream.metadata,
          childTableName,
          position,
          false, // isPrimaryNode
          upstream.columns // Pass columns from new API structure
        );
        nodes.push(node);
      }

      // Create edge from child to parent (always create edge if both nodes exist)
      const edgeId = `e${nodeId}-${parentNodeId}`;
      if (!edges.find((e) => e.id === edgeId)) {
        edges.push({
          id: edgeId,
          source: nodeId,
          target: parentNodeId,
          type: "bezier",
          animated: false,
          style: {
            stroke: '#374151',
            strokeWidth: 3,
          },
          markerEnd: {
            type: 'arrowclosed',
            color: '#374151',
            width: 20,
            height: 20
          },
        });
      }

      // Process parents recursively
      if (upstream.parents && upstream.parents.length > 0) {
        processUpstreams(upstream.parents, nodeId, level + 1);
      }
    });
  };

  // Helper function to process downstreams recursively
  const processDownstreams = (downstreams, parentNodeId, level = 0) => {
    if (!downstreams || !Array.isArray(downstreams)) return;

    // Get parent node to calculate proper positioning
    const parentNode = nodes.find(n => n.id === parentNodeId);
    const parentY = parentNode ? parentNode.position.y : 700;
    
    // Calculate spacing based on number of downstreams
    const totalDownstreams = downstreams.length;
    const spacing = totalDownstreams === 1 ? 0 : 250; // No spacing for single child, 250px for multiple
    const startY = totalDownstreams === 1 ? parentY : parentY - (totalDownstreams - 1) * spacing / 2;

    downstreams.forEach((downstream, index) => {
      const childTableName = downstream.parent || downstream.child;
      
      // Skip if no table name is available
      if (!childTableName) return;
      
      const nodeId = generateNodeId(childTableName);

      // Create node if it doesn't exist (regardless of metadata)
      if (!nodes.find((n) => n.id === nodeId)) {
        const position = {
          x: 500 + (level + 1) * 600, // Increased horizontal spacing
          y: startY + index * spacing,
        };
        const node = createNodeFromMetadata(
          downstream.metadata,
          childTableName,
          position,
          false, // isPrimaryNode
          downstream.columns // Pass columns from new API structure
        );
        nodes.push(node);
      }

      // Create edge from parent to child (always create edge if both nodes exist)
      const edgeId = `e${parentNodeId}-${nodeId}`;
      if (!edges.find((e) => e.id === edgeId)) {
        edges.push({
          id: edgeId,
          source: parentNodeId,
          target: nodeId,
          type: "bezier",
          animated: false,
          style: {
            stroke: '#374151',
            strokeWidth: 3,
          },
          markerEnd: {
            type: 'arrowclosed',
            color: '#374151',
            width: 20,
            height: 20
          },
        });
      }

      // Process children recursively
      if (downstream.children && downstream.children.length > 0) {
        processDownstreams(downstream.children, nodeId, level + 1);
      }
    });
  };

  // Process main element
  const mainTableName = apiResponse.name;
  const mainNodeId = generateNodeId(mainTableName);

  // Create main node (primary node)
  const mainNode = createNodeFromMetadata(
    apiResponse.metadata, 
    mainTableName, 
    { x: 500, y: 700 }, 
    true, // Mark as primary node
    apiResponse.columns // Pass columns from new API structure
  );
  nodes.push(mainNode);

  // Process upstreams
  if (apiResponse.upstreams) {
    processUpstreams(apiResponse.upstreams, mainNodeId);
  }

  // Process downstreams
  if (apiResponse.downstreams) {
    processDownstreams(apiResponse.downstreams, mainNodeId);
  }

  return { nodes, edges };
};

// Table Details Modal Component
const TableDetailsModal = ({ isOpen, onClose, onSubmit, editingNode, nodes }) => {
  const [formData, setFormData] = useState({
    tableName: '',
    description: '',
    columns: [{ name: '', dataType: '', description: '' }]
  });

  // Update form data when editingNode changes
  React.useEffect(() => {
    if (editingNode) {
      const node = nodes.find(n => n.id === editingNode);
      if (node) {
        setFormData({
          tableName: node.data.tableName || '',
          description: node.data.description || '',
          columns: node.data.allColumns?.map(col => ({
            name: col.name || '',
            dataType: col.type || '',
            description: col.description || ''
          })) || [{ name: '', dataType: '', description: '' }]
        });
      }
    } else {
      setFormData({
        tableName: '',
        description: '',
        columns: [{ name: '', dataType: '', description: '' }]
      });
    }
  }, [editingNode, nodes]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleColumnChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      columns: prev.columns.map((col, i) => 
        i === index ? { ...col, [field]: value } : col
      )
    }));
  };

  const addColumn = () => {
    setFormData(prev => ({
      ...prev,
      columns: [...prev.columns, { name: '', dataType: '', description: '' }]
    }));
  };

  const removeColumn = (index) => {
    if (formData.columns.length > 1) {
      setFormData(prev => ({
        ...prev,
        columns: prev.columns.filter((_, i) => i !== index)
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  if (!isOpen) return null;

  return (
    <div className="viewlineage-modal-overlay" onClick={onClose}>
      <div className="viewlineage-table-details-modal" onClick={(e) => e.stopPropagation()}>
        <div className="viewlineage-modal-header">
          <h3>{editingNode ? 'Edit Table' : 'Add New Table'}</h3>
          <button className="viewlineage-modal-close" onClick={onClose}>
            ×
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="viewlineage-modal-form">
          <div className="viewlineage-form-group">
            <label htmlFor="tableName">Table Name *</label>
            <input
              type="text"
              id="tableName"
              value={formData.tableName}
              onChange={(e) => handleInputChange('tableName', e.target.value)}
              required
              placeholder="Enter table name"
            />
          </div>

          <div className="viewlineage-form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Enter table description"
              rows="3"
            />
          </div>

          <div className="viewlineage-form-group">
            <div className="viewlineage-columns-header">
              <label>Columns</label>
              <button type="button" onClick={addColumn} className="viewlineage-add-column-btn">
                + Add Column
              </button>
            </div>
            
            <div className="viewlineage-columns-list">
              {formData.columns.map((column, index) => (
                <div key={index} className="viewlineage-column-item">
                  <div className="viewlineage-column-inputs">
                    <input
                      type="text"
                      placeholder="Column name"
                      value={column.name}
                      onChange={(e) => handleColumnChange(index, 'name', e.target.value)}
                    />
                    <input
                      type="text"
                      placeholder="Data type"
                      value={column.dataType}
                      onChange={(e) => handleColumnChange(index, 'dataType', e.target.value)}
                    />
                    <input
                      type="text"
                      placeholder="Description"
                      value={column.description}
                      onChange={(e) => handleColumnChange(index, 'description', e.target.value)}
                    />
                  </div>
                  {formData.columns.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeColumn(index)}
                      className="viewlineage-remove-column-btn"
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="viewlineage-modal-actions">
            <button type="button" onClick={onClose} className="viewlineage-cancel-btn">
              Cancel
            </button>
            <button type="submit" className="viewlineage-save-btn">
              {editingNode ? 'Update Table' : 'Create Table'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Custom Table Node Component
const TableNode = ({ data, selected, id }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { tableName, visibleColumns, showMoreText, allColumns, owner, description, isEmpty, isPrimaryNode, onExpandNode, isExpanding, onColumnLineage, isColumnLineageLoading, hasColumnLineage, connectedColumns, onColumnClick, highlightedColumn } = data;

  // Determine which columns to show based on expansion state or column lineage
  // Auto-expand when column lineage is active to show all column handles
  const shouldShowAllColumns = isExpanded || hasColumnLineage;
  const displayColumns = shouldShowAllColumns ? allColumns : visibleColumns;
  const toggleText = isExpanded ? "Hide columns" : showMoreText;

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  const handleExpandClick = (e) => {
    e.stopPropagation(); // Prevent node selection
    if (onExpandNode) {
      onExpandNode(id, tableName);
    }
  };

  const handleColumnLineageClick = (e) => {
    e.stopPropagation(); // Prevent node selection
    if (onColumnLineage) {
      onColumnLineage(id, tableName);
    }
  };

  const handleColumnClick = (e, columnName) => {
    e.stopPropagation(); // Prevent node selection
    if (onColumnClick && hasColumnLineage) {
      onColumnClick(id, columnName, tableName);
    }
  };

  const normalizedName = (tableName || '').toLowerCase();
  const isAdlsSource = normalizedName.includes('abfss://');

  const tableIcon = useMemo(() => {

    if (isAdlsSource) {
      return {
        variant: 'single',
        content: (
          <div className="table-icon-dual-inner">

            <img
              src={AdlsIcon}
              alt="ADLS icon"
              className="table-icon-img"
            />
          </div>
        ),
      };
    }

    return {
      variant: 'dual',
      content: (
        <div className="table-icon-dual-inner">
          <img
            src={AzureDatabricksSVG}
            alt="Azure Databricks"
            className="table-icon-dual-img"
          />
          <img
            src={DatabricksSVG}
            alt="Databricks"
            className="table-icon-dual-img"
          />
        </div>
      ),
    };
  }, [isAdlsSource]);

  return (
    <div 
      className={`table-node ${selected ? "table-node-selected" : ""} ${isPrimaryNode ? "table-node-primary" : ""}`}
      data-is-empty={isEmpty ? "true" : "false"}
      data-is-primary={isPrimaryNode ? "true" : "false"}
      data-has-column-lineage={hasColumnLineage ? "true" : "false"}
    >
      {/* Table Header */}
      <div className="table-header">
          {tableIcon.content}
      
        <div className="table-info">
          <div className="table-label">{isAdlsSource ? "File" : "Table"}</div>
          <div className="table-name" title={tableName}>
            {tableName}
          </div>
          {description && (
            <div className="table-description" title={description}>
              {description}
            </div>
          )}
          
        </div>
        {!isEmpty && (
          <div 
            className={`table-action ${isExpanding ? 'table-action-loading' : ''}`}
            onClick={handleExpandClick} 
            title={isExpanding ? "Loading..." : "Expand node to load children/parents"}
          >
            {isExpanding ? (
              <div className="expand-spinner"></div>
            ) : (
          <svg className="action-icon" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
              clipRule="evenodd"
            ></path>
          </svg>
            )}
          </div>
        )}
      </div>

      {/* Table Columns */}
      <div className="table-columns">
        {displayColumns.map((column, index) => {
          const isConnected = connectedColumns && connectedColumns.includes(column.name);
          // Check if this column is highlighted (clicked in this node) OR if it's the related column in another node
          const isDirectlyHighlighted = highlightedColumn && highlightedColumn.nodeId === id && highlightedColumn.columnName === column.name;
          const isRelatedHighlighted = highlightedColumn && highlightedColumn.nodeId !== id && 
                                       highlightedColumn.relatedColumns && 
                                       highlightedColumn.relatedColumns.some(rc => rc.nodeId === id && rc.columnName === column.name);
          const isHighlighted = isDirectlyHighlighted || isRelatedHighlighted;
          
          return (
            <div 
              key={index} 
              className={`column-row ${isConnected ? 'column-connected' : ''} ${isHighlighted ? 'column-highlighted' : ''} ${hasColumnLineage ? 'column-clickable' : ''}`} 
              style={{ position: 'relative' }}
              onClick={(e) => handleColumnClick(e, column.name)}
            >
            <span className="column-name">{column.name}</span>
            <span className="column-type">{column.type}</span>
              {/* Column-specific handles for lineage connections */}
              <Handle
                type="target"
                position={Position.Left}
                id={`col-${column.name}`}
                className={`column-handle column-handle-left ${isConnected ? 'column-handle-connected' : ''} ${isHighlighted ? 'column-handle-highlighted' : ''}`}
                style={{ 
                  left: -6, 
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: '6px',
                  height: '6px'
                }}
              />
              <Handle
                type="source"
                position={Position.Right}
                id={`col-${column.name}`}
                className={`column-handle column-handle-right ${isConnected ? 'column-handle-connected' : ''} ${isHighlighted ? 'column-handle-highlighted' : ''}`}
                style={{ 
                  right: -6, 
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: '6px',
                  height: '6px'
                }}
              />
          </div>
          );
        })}

        {showMoreText && !hasColumnLineage && (
          <div className="show-more-container">
            <button className="show-more-btn" onClick={handleToggle}>
              {toggleText}
            </button>
          </div>
        )}
      </div>

      {/* Column Lineage Button - Only show if node has columns */}
      {!isEmpty && allColumns && allColumns.length > 0 && (
        <div className="column-lineage-button-container">
          <button 
            className={`column-lineage-btn ${hasColumnLineage ? 'active' : ''} ${isColumnLineageLoading ? 'loading' : ''}`}
            onClick={handleColumnLineageClick}
            disabled={isColumnLineageLoading}
            title={hasColumnLineage ? "Hide Column Lineage" : "Show Column Lineage"}
          >
            {isColumnLineageLoading ? (
              <>
                <div className="column-lineage-spinner"></div>
                <span>Loading...</span>
              </>
            ) : (
              <>
                <svg className="column-lineage-icon" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M3 4a1 1 0 011-1h4a1 1 0 010 2H6.414l2.293 2.293a1 1 0 01-1.414 1.414L5 6.414V8a1 1 0 01-2 0V4zm9 1a1 1 0 110-2h4a1 1 0 011 1v4a1 1 0 11-2 0V6.414l-2.293 2.293a1 1 0 11-1.414-1.414L13.586 5H12zm-9 7a1 1 0 112 0v1.586l2.293-2.293a1 1 0 011.414 1.414L6.414 15H8a1 1 0 110 2H4a1 1 0 01-1-1v-4zm13-1a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 110-2h1.586l-2.293-2.293a1 1 0 011.414-1.414L15 13.586V12a1 1 0 011-1z" clipRule="evenodd"></path>
                </svg>
                <span>{hasColumnLineage ? "Hide Column Lineage" : "Column Lineage"}</span>
              </>
            )}
          </button>
        </div>
      )}

      {/* Empty node indicator */}
      {isEmpty && (
        <div className="empty-node-indicator">
          <div className="empty-node-text">Click to configure</div>
        </div>
      )}

      {/* Connection Handles - Positioned at header level with custom IDs */}
      <Handle
        type="target"
        position={Position.Left}
        id="header-left"
        className="connection-handle header-handle"
        data-handlepos="left"
        style={{
          top: '25px !important', // Position at header level
          left: '-10px !important',
          transform: 'translateY(-50%) !important',
          position: 'absolute !important',
          zIndex: 10
        }}
      />
      <Handle
        type="source"
        position={Position.Right}
        id="header-right"
        className="connection-handle header-handle"
        data-handlepos="right"
        style={{
          top: '25px !important', // Position at header level
          right: '-10px !important',
          transform: 'translateY(-50%) !important',
          position: 'absolute !important',
          zIndex: 10
        }}
      />
    </div>
  );
};

const nodeTypes = {
  tableNode: TableNode,
};

const ViewLineage = ({ contractData }) => {
  const [selectedNode, setSelectedNode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [apiData, setApiData] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingNode, setEditingNode] = useState(null);
  const [nodeCounter, setNodeCounter] = useState(1000); // Start from 1000 to avoid conflicts with API nodes
  const [expandingNodeId, setExpandingNodeId] = useState(null); // Track which node is being expanded
  const [columnLineageLoadingNodeId, setColumnLineageLoadingNodeId] = useState(null); // Track column lineage loading
  const [columnLineageData, setColumnLineageData] = useState({}); // Store column lineage data by node ID
  const [columnLineageEdges, setColumnLineageEdges] = useState([]); // Store column lineage edges
  const [highlightedColumn, setHighlightedColumn] = useState(null); // Track highlighted column {nodeId, columnName, tableName}
  const contractName = contractData ? contractData.name.replace('|', '.') : '';
  const filePath = contractData ? contractData.filePath : '';
  const sourceType = contractData ? contractData.source_type : '';

  const API_URL = `${process.env.REACT_APP_API_URL}/lineage?table_name=${filePath}&source_type=${sourceType}`;

  // Fetch data from API
  const fetchLineageData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(API_URL);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      setApiData(data);
    } catch (err) {
      console.error('Failed to get access token:', err.response?.data || err.message);
      setError(err.message);
      // Fallback to dummy data
      //   setApiData(dummyData);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch data on component mount
  useEffect(() => {
    fetchLineageData();
  }, [fetchLineageData]);

  // Generate nodes and edges from API data
  const { nodes: generatedNodes, edges: generatedEdges } = useMemo(() => {
    if (apiData) {
      return mapApiResponseToLineageGraph(apiData);
    }
    return { nodes: [], edges: [] };
  }, [apiData]);

  const [nodes, setNodes] = useNodesState(generatedNodes);
  const [edges, setEdges] = useEdgesState(generatedEdges);

  // Update nodes and edges when API data changes
  useEffect(() => {
    setNodes(generatedNodes);
    setEdges(generatedEdges);
  }, [generatedNodes, generatedEdges, setNodes, setEdges]);

  const onNodesChange = useCallback(
    (changes) =>
      setNodes((nodesSnapshot) => applyNodeChanges(changes, nodesSnapshot)),
    []
  );
  const onEdgesChange = useCallback(
    (changes) =>
      setEdges((edgesSnapshot) => applyEdgeChanges(changes, edgesSnapshot)),
    []
  );

  const onConnect = useCallback(
    (params) => setEdges((eds) => addEdge(params, eds)),
    [setEdges]
  );

  // Edit Table functionality (defined before onNodeClick to avoid reference error)
  const handleEditTable = useCallback((nodeId) => {
    setEditingNode(nodeId);
    setIsModalOpen(true);
  }, []);

  const onNodeClick = useCallback(
    (event, node) => {
      setSelectedNode(selectedNode === node.id ? null : node.id);
      if (node.data.isEmpty) { // Single click for empty nodes
        handleEditTable(node.id);
      } else if (event.detail === 2) { // Double click for existing nodes
        handleEditTable(node.id);
      }
    },
    [selectedNode, handleEditTable]
  );

  const onPaneClick = useCallback(() => {
    setSelectedNode(null);
  }, []);

  // Add Table functionality
  const handleAddTable = useCallback(() => {
    const newNodeId = nodeCounter.toString();
    setNodeCounter(prev => prev + 1);
    const emptyNode = {
      id: newNodeId,
      type: "tableNode",
      position: { x: Math.random() * 400 + 100, y: Math.random() * 300 + 100 },
      data: {
        tableName: "new_table",
        description: "New table description",
        owner: "User",
        allColumns: [{ name: "abc", type: "VARCHAR(255)" }],
        visibleColumns: [{ name: "abc", type: "VARCHAR(255)" }],
        showMoreText: null,
        isEmpty: true // Flag to indicate this is an empty node
      }
    };
    setNodes((currentNodes) => [...currentNodes, emptyNode]);
  }, [nodeCounter, setNodes]);

  // Modal submit handler
  const handleModalSubmit = useCallback((formData) => {
    if (editingNode) {
      // Update existing node
      setNodes((currentNodes) =>
        currentNodes.map((node) =>
          node.id === editingNode
            ? {
                ...node,
                data: {
                  ...node.data,
                  tableName: formData.tableName,
                  description: formData.description,
                  allColumns: formData.columns.map(col => ({
                    name: col.name,
                    type: col.dataType
                  })),
                  visibleColumns: formData.columns.slice(0, 3).map(col => ({
                    name: col.name,
                    type: col.dataType
                  })),
                  showMoreText: formData.columns.length > 3 ? `Show ${formData.columns.length - 3} more columns` : null,
                  isEmpty: false // Mark as no longer empty
                }
              }
            : node
        )
      );
    } else {
      // Create new node (this shouldn't happen with current flow, but keeping for safety)
      const newNodeId = nodeCounter.toString();
      setNodeCounter(prev => prev + 1);
      const newNode = {
        id: newNodeId,
        type: "tableNode",
        position: { x: Math.random() * 400 + 100, y: Math.random() * 300 + 100 },
        data: {
          tableName: formData.tableName,
          description: formData.description,
          owner: "User",
          allColumns: formData.columns.map(col => ({
            name: col.name,
            type: col.dataType
          })),
          visibleColumns: formData.columns.slice(0, 3).map(col => ({
            name: col.name,
            type: col.dataType
          })),
          showMoreText: formData.columns.length > 3 ? `Show ${formData.columns.length - 3} more columns` : null,
          isEmpty: false
        }
      };
      setNodes((currentNodes) => [...currentNodes, newNode]);
    }
    setIsModalOpen(false);
    setEditingNode(null);
  }, [editingNode, nodeCounter, setNodes]);

  // Modal close handler
  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
    setEditingNode(null);
  }, []);

  // Delete node functionality
  const handleDeleteNode = useCallback(() => {
    if (selectedNode) {
      const node = nodes.find(n => n.id === selectedNode);
      const nodeName = node?.data?.tableName || 'this node';
      // if (window.confirm(`Are you sure you want to delete "${nodeName}"? This action cannot be undone.`)) {
      // }
      setNodes((currentNodes) => currentNodes.filter(node => node.id !== selectedNode));
      setEdges((currentEdges) => currentEdges.filter(edge => 
        edge.source !== selectedNode && edge.target !== selectedNode
      ));
      setSelectedNode(null);
    }
  }, [selectedNode, setNodes, setEdges, nodes]);

  // Expand node functionality - fetch children/parents for a specific node
  const handleExpandNode = useCallback(async (nodeId, tableName) => {
    setExpandingNodeId(nodeId); // Set loading state for this specific node
    
    try {
      const expandApiUrl = `${process.env.REACT_APP_API_URL}/lineage?table_name=${filePath}&source_type=${sourceType}`;
      const response = await fetch(expandApiUrl);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Get the current node to use as parent for positioning
      const currentNode = nodes.find(n => n.id === nodeId);
      if (!currentNode) return;

      // Create a temporary node ID map to track existing nodes by table name
      const existingNodesByTableName = new Map();
      nodes.forEach(node => {
        if (node.data.tableName) {
          existingNodesByTableName.set(node.data.tableName, node.id);
        }
      });

      // Process the API response manually to avoid duplicate node creation
      const nodesToAdd = [];
      const edgesToAdd = [];
      let tempNodeCounter = 10000; // Start from high number to avoid conflicts

      // Helper function to get or create node ID
      const getOrCreateNodeId = (tableName) => {
        if (existingNodesByTableName.has(tableName)) {
          return existingNodesByTableName.get(tableName);
        }
        const newId = tempNodeCounter.toString();
        tempNodeCounter++;
        existingNodesByTableName.set(tableName, newId);
        return newId;
      };

      // Helper function to create node from metadata
      const createNodeFromMetadataForExpansion = (metadata, tableName, position, columns = null) => {
        const nodeId = getOrCreateNodeId(tableName);
        
        // Use columns parameter first, then fall back to metadata.columns
        const columnsToUse = columns || (metadata && metadata.columns) || [];
        
        // Handle empty arrays - return early with empty columns
        if (!Array.isArray(columnsToUse) || columnsToUse.length === 0) {
          return {
            id: nodeId,
            type: "tableNode",
            position,
            data: {
              tableName: tableName,
              owner: (metadata && metadata.owner) || "Unknown",
              visibleColumns: [],
              allColumns: [],
              showMoreText: null,
              isPrimaryNode: false,
            },
          };
        }
        
        // Handle both old format (objects) and new format (strings)
        const allColumns = columnsToUse.map((col) => {
          if (typeof col === 'string') {
            return {
              name: col,
              type: "string",
            };
          } else {
            return {
              name: col.name || "",
              type: col.type_name || col.type_text || "unknown",
            };
          }
        });

        const visibleColumns = allColumns.slice(0, 3);
        const remainingCount = allColumns.length - 3;
        const showMoreText = remainingCount > 0 ? `Show ${remainingCount} more columns` : null;

        return {
          id: nodeId,
          type: "tableNode",
          position,
          data: {
            tableName: tableName,
            owner: (metadata && metadata.owner) || "Unknown",
            visibleColumns,
            allColumns,
            showMoreText,
            isPrimaryNode: false,
          },
        };
      };

      // Process upstreams (parents of the clicked node)
      if (data.upstreams && Array.isArray(data.upstreams)) {
        data.upstreams.forEach((upstream, index) => {
          const parentTableName = upstream.child;
          if (!parentTableName) return;

          const parentNodeId = getOrCreateNodeId(parentTableName);
          
          // Only create node if it doesn't exist
          if (!existingNodesByTableName.has(parentTableName) || 
              !nodes.find(n => n.data.tableName === parentTableName)) {
            const position = {
              x: Math.max(0, currentNode.position.x - 600),
              y: currentNode.position.y + (index - data.upstreams.length / 2) * 250,
            };
            const node = createNodeFromMetadataForExpansion(
              upstream.metadata, 
              parentTableName, 
              position, 
              upstream.columns // Pass columns from new API structure
            );
            nodesToAdd.push(node);
          }

          // Create edge from parent to clicked node
          const edgeId = `e${parentNodeId}-${nodeId}`;
          if (!edges.find(e => e.id === edgeId)) {
            edgesToAdd.push({
              id: edgeId,
              source: parentNodeId,
              target: nodeId,
              type: "bezier",
              animated: false,
              style: {
                stroke: '#374151',
                strokeWidth: 3,
              },
              markerEnd: {
                type: 'arrowclosed',
                color: '#374151',
                width: 20,
                height: 20
              },
            });
          }
        });
      }

      // Process downstreams (children of the clicked node)
      if (data.downstreams && Array.isArray(data.downstreams)) {
        data.downstreams.forEach((downstream, index) => {
          const childTableName = downstream.parent;
          if (!childTableName) return;

          const childNodeId = getOrCreateNodeId(childTableName);
          
          // Only create node if it doesn't exist
          if (!existingNodesByTableName.has(childTableName) || 
              !nodes.find(n => n.data.tableName === childTableName)) {
            const position = {
              x: currentNode.position.x + 600,
              y: currentNode.position.y + (index - data.downstreams.length / 2) * 250,
            };
            const node = createNodeFromMetadataForExpansion(
              downstream.metadata, 
              childTableName, 
              position,
              downstream.columns // Pass columns from new API structure
            );
            nodesToAdd.push(node);
          }

          // Create edge from clicked node to child
          const edgeId = `e${nodeId}-${childNodeId}`;
          if (!edges.find(e => e.id === edgeId)) {
            edgesToAdd.push({
              id: edgeId,
              source: nodeId,
              target: childNodeId,
              type: "bezier",
              animated: false,
              style: {
                stroke: '#374151',
                strokeWidth: 3,
              },
              markerEnd: {
                type: 'arrowclosed',
                color: '#374151',
                width: 20,
                height: 20
              },
            });
          }
        });
      }

      // Add new nodes and edges to the graph
      if (nodesToAdd.length > 0) {
        setNodes((currentNodes) => [...currentNodes, ...nodesToAdd]);
      }
      if (edgesToAdd.length > 0) {
        setEdges((currentEdges) => [...currentEdges, ...edgesToAdd]);
      }

    } catch (err) {
      console.error('Failed to expand node:', err.message);
      // You can add a toast notification here if needed
    } finally {
      setExpandingNodeId(null); // Clear loading state
    }
  }, [nodes, edges, setNodes, setEdges]);

  // Column Lineage functionality - fetch and display column-level lineage
  const handleColumnLineage = useCallback(async (nodeId, tableName) => {
    // Check if column lineage is already displayed for this node
    if (columnLineageData[nodeId]) {
      // Get all edges that belong to this node
      const edgesToRemove = columnLineageEdges.filter(edge => edge.id.startsWith(`col-${nodeId}-`));
      
      // Collect all affected node IDs from the edges being removed
      const affectedNodeIds = new Set([nodeId]);
      edgesToRemove.forEach(edge => {
        affectedNodeIds.add(edge.source);
        affectedNodeIds.add(edge.target);
      });
      
      // Remove column lineage for this node
      setColumnLineageData((prevData) => {
        const newData = { ...prevData };
        delete newData[nodeId];
        return newData;
      });
      
      // Remove column lineage edges for this node
      setColumnLineageEdges((prevEdges) => 
        prevEdges.filter(edge => !edge.id.startsWith(`col-${nodeId}-`))
      );
      
      // Check if any of the affected nodes still have column lineage from other nodes
      const remainingAffectedNodes = new Set();
      columnLineageEdges.forEach(edge => {
        // Skip edges that will be removed
        if (!edge.id.startsWith(`col-${nodeId}-`)) {
          if (affectedNodeIds.has(edge.source)) {
            remainingAffectedNodes.add(edge.source);
          }
          if (affectedNodeIds.has(edge.target)) {
            remainingAffectedNodes.add(edge.target);
          }
        }
      });
      
      setNodes((currentNodes) => 
        currentNodes.map(node => {
          if (affectedNodeIds.has(node.id) && !remainingAffectedNodes.has(node.id)) {
            return { ...node, data: { ...node.data, hasColumnLineage: false } };
          }
          return node;
        })
      );
      
      return;
    }

    setColumnLineageLoadingNodeId(nodeId); // Set loading state

    try {
      const columnLineageApiUrl = `${process.env.REACT_APP_API_URL}/column-lineage?table_name=${tableName}&source_type=${sourceType}`;
      const response = await fetch(columnLineageApiUrl);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Store the column lineage data
      setColumnLineageData((prevData) => ({
        ...prevData,
        [nodeId]: data,
      }));

      // Create column lineage edges
      const newColumnEdges = [];
      const nodesByTableName = new Map();
      const affectedNodeIds = new Set([nodeId]); // Start with clicked node
      
      // Helper function to normalize table names for matching
      const normalizeTableName = (tableName) => {
        if (!tableName) return '';
        // Remove any special characters and convert to lowercase for comparison
        return tableName.toLowerCase().replace(/[^a-z0-9._]/g, '');
      };
      
      // Helper function to find node by table name (flexible matching)
      const findNodeByTableName = (tableName) => {
        if (!tableName) return null;
        const normalized = normalizeTableName(tableName);
        
        // First try exact match
        for (const node of nodes) {
          if (node.data.tableName && normalizeTableName(node.data.tableName) === normalized) {
            return node.id;
          }
        }
        
        // Try partial match (table name contains or is contained in node name)
        for (const node of nodes) {
          const nodeTableName = node.data.tableName;
          if (nodeTableName) {
            const nodeNormalized = normalizeTableName(nodeTableName);
            // Check if either contains the other (for flexible matching)
            if (normalized.includes(nodeNormalized) || nodeNormalized.includes(normalized)) {
              return node.id;
            }
            // Also try matching the last part (table name after last dot)
            const tableLastPart = normalized.split('.').pop();
            const nodeLastPart = nodeNormalized.split('.').pop();
            if (tableLastPart && nodeLastPart && tableLastPart === nodeLastPart) {
              return node.id;
            }
          }
        }
        
        return null;
      };
      
      // Build a map of table names to node IDs (with flexible matching)
      nodes.forEach(node => {
        if (node.data.tableName) {
          const normalized = normalizeTableName(node.data.tableName);
          nodesByTableName.set(normalized, node.id);
          // Also store by last part of table name
          const lastPart = normalized.split('.').pop();
          if (lastPart) {
            nodesByTableName.set(lastPart, node.id);
          }
        }
      });

      // Process upstream lineage (parent columns -> current node columns)
      if (data['upstream-lineage'] && Array.isArray(data['upstream-lineage'])) {
        data['upstream-lineage'].forEach((lineage, index) => {
          const sourceTableName = lineage.source_lineage;
          const targetTableName = lineage.target_lineage;
          
          const sourceNodeId = findNodeByTableName(sourceTableName);
          const targetNodeId = findNodeByTableName(targetTableName);

          // Add both source and target nodes to affected nodes
          if (sourceNodeId) {
            affectedNodeIds.add(sourceNodeId);
          } else {
            console.warn(`⚠️ Source node not found for table: ${sourceTableName}`);
          }
          
          if (targetNodeId) {
            affectedNodeIds.add(targetNodeId);
          } else {
            console.warn(`⚠️ Target node not found for table: ${targetTableName}`);
          }

          // Only create edge if both nodes exist
          if (sourceNodeId && targetNodeId) {
            newColumnEdges.push({
              id: `col-${nodeId}-up-${index}`,
              source: sourceNodeId,
              target: targetNodeId,
              sourceHandle: `col-${lineage.source_column_name}`,
              targetHandle: `col-${lineage.target_column_name}`,
              type: "bezier",
              animated: false,
              style: { 
                stroke: '#f59e0b', 
                strokeWidth: 2,
                strokeDasharray: '0'
              },
              markerEnd: {
                type: 'arrowclosed',
                color: '#f59e0b',
                width: 20,
                height: 20
              },
              data: {
                isColumnLineage: true,
                sourceColumn: lineage.source_column_name,
                targetColumn: lineage.target_column_name,
                nodeId: nodeId,
                edgeType: 'upstream'
              },
            });
          }
        });
      }

      // Process downstream lineage (current node columns -> child columns)
      if (data['downstream-lineage'] && Array.isArray(data['downstream-lineage'])) {
        data['downstream-lineage'].forEach((lineage, index) => {
          const sourceTableName = lineage.source_lineage;
          const targetTableName = lineage.target_lineage;
          
          const sourceNodeId = findNodeByTableName(sourceTableName);
          const targetNodeId = findNodeByTableName(targetTableName);

          // Add both source and target nodes to affected nodes
          if (sourceNodeId) {
            affectedNodeIds.add(sourceNodeId);
          } else {
            console.warn(`⚠️ Source node not found for table: ${sourceTableName}`);
          }
          
          if (targetNodeId) {
            affectedNodeIds.add(targetNodeId);
          } else {
            console.warn(`⚠️ Target node not found for table: ${targetTableName}`);
          }

          // Only create edge if both nodes exist
          if (sourceNodeId && targetNodeId) {
            newColumnEdges.push({
              id: `col-${nodeId}-down-${index}`,
              source: sourceNodeId,
              target: targetNodeId,
              sourceHandle: `col-${lineage.source_column_name}`,
              targetHandle: `col-${lineage.target_column_name}`,
              type: "bezier",
              animated: false,
              style: { 
                stroke: '#10b981', 
                strokeWidth: 2,
                strokeDasharray: '0'
              },
              markerEnd: {
                type: 'arrowclosed',
                color: '#10b981',
                width: 20,
                height: 20
              },
              data: {
                isColumnLineage: true,
                sourceColumn: lineage.source_column_name,
                targetColumn: lineage.target_column_name,
                nodeId: nodeId,
                edgeType: 'downstream'
              },
            });
          }
        });
      }
      
      setColumnLineageEdges((prevEdges) => [...prevEdges, ...newColumnEdges]);

      // Immediately update nodes to set hasColumnLineage flag
      setNodes((currentNodes) => {
        const updatedNodes = currentNodes.map(node => {
          if (affectedNodeIds.has(node.id)) {
            return { 
              ...node, 
              data: { 
                ...node.data, 
                hasColumnLineage: true 
              } 
            };
          }
          return node;
        });
        return updatedNodes;
      });

    } catch (err) {
      console.error('Failed to fetch column lineage:', err.message);
      alert(`Failed to fetch column lineage: ${err.message}`);
    } finally {
      setColumnLineageLoadingNodeId(null); // Clear loading state
    }
  }, [nodes, columnLineageData, setColumnLineageEdges, setNodes]);

  // Handle column click to highlight related edges
  const handleColumnClick = useCallback((nodeId, columnName, tableName) => {
    // If clicking the same column, deselect it
    if (highlightedColumn && 
        highlightedColumn.nodeId === nodeId && 
        highlightedColumn.columnName === columnName) {
      setHighlightedColumn(null);
    } else {
      // Find all related columns through column lineage edges
      const relatedColumns = [];
      const processedPairs = new Set(); // To avoid duplicates
      
      columnLineageEdges.forEach(edge => {
        // Check if this edge involves the clicked column
        if (edge.data && edge.data.isColumnLineage) {
          // If the clicked column is the source
          if (edge.source === nodeId && edge.data.sourceColumn === columnName) {
            const pairKey = `${edge.target}-${edge.data.targetColumn}`;
            if (!processedPairs.has(pairKey)) {
              relatedColumns.push({
                nodeId: edge.target,
                columnName: edge.data.targetColumn
              });
              processedPairs.add(pairKey);
            }
          }
          // If the clicked column is the target
          else if (edge.target === nodeId && edge.data.targetColumn === columnName) {
            const pairKey = `${edge.source}-${edge.data.sourceColumn}`;
            if (!processedPairs.has(pairKey)) {
              relatedColumns.push({
                nodeId: edge.source,
                columnName: edge.data.sourceColumn
              });
              processedPairs.add(pairKey);
            }
          }
        }
      });
      
      // Highlight the clicked column and all related columns
      setHighlightedColumn({ nodeId, columnName, tableName, relatedColumns });
    }
  }, [highlightedColumn, columnLineageEdges]);

  // Update nodes to include selected state and expand handler
  const nodesWithSelection = useMemo(() => {
    // Collect all connected columns based on actual column lineage edges
    const allConnectedColumns = new Map(); // nodeId -> Set of connected columns
    
    // Process all column lineage edges to find connected columns
    columnLineageEdges.forEach(edge => {
      if (edge.data && edge.data.isColumnLineage) {
        // Add source column to source node
        if (!allConnectedColumns.has(edge.source)) {
          allConnectedColumns.set(edge.source, new Set());
        }
        allConnectedColumns.get(edge.source).add(edge.data.sourceColumn);
        
        // Add target column to target node
        if (!allConnectedColumns.has(edge.target)) {
          allConnectedColumns.set(edge.target, new Set());
        }
        allConnectedColumns.get(edge.target).add(edge.data.targetColumn);
      }
    });

    return nodes.map((node) => {
      // Get connected columns for this specific node
      const nodeConnectedColumns = allConnectedColumns.get(node.id) || new Set();

      return {
      ...node,
      data: {
        ...node.data,
        selected: selectedNode === node.id,
          onExpandNode: handleExpandNode,
          isExpanding: expandingNodeId === node.id,
          onColumnLineage: handleColumnLineage,
          isColumnLineageLoading: columnLineageLoadingNodeId === node.id,
          hasColumnLineage: !!columnLineageData[node.id],
          connectedColumns: Array.from(nodeConnectedColumns), // Pass all connected columns to node
          onColumnClick: handleColumnClick,
          highlightedColumn: highlightedColumn,
        },
      };
    });
  }, [nodes, selectedNode, handleExpandNode, expandingNodeId, handleColumnLineage, columnLineageLoadingNodeId, columnLineageData, columnLineageEdges, handleColumnClick, highlightedColumn]);

  // Merge regular edges with column lineage edges and apply highlighting
  const allEdges = useMemo(() => {
    const merged = [...edges, ...columnLineageEdges].map(edge => {
      // Check if this edge should be highlighted based on the clicked column
      if (highlightedColumn && edge.data && edge.data.isColumnLineage) {
        // Check if edge connects to the clicked column
        const isDirectlyRelated = 
          (edge.data.sourceColumn === highlightedColumn.columnName || 
           edge.data.targetColumn === highlightedColumn.columnName) &&
          (edge.source === highlightedColumn.nodeId || edge.target === highlightedColumn.nodeId);
        
        // Check if edge connects to any related column
        const isRelatedToRelatedColumn = highlightedColumn.relatedColumns && 
          highlightedColumn.relatedColumns.some(rc => {
            return (
              (edge.source === rc.nodeId && edge.data.sourceColumn === rc.columnName) ||
              (edge.target === rc.nodeId && edge.data.targetColumn === rc.columnName)
            );
          });
        
        if (isDirectlyRelated || isRelatedToRelatedColumn) {
          // Highlight the edge
          return {
            ...edge,
            animated: true,
            style: {
              ...edge.style,
              stroke: '#8b5cf6', // Purple color for highlighted edges
              strokeWidth: 4,
            },
            markerEnd: {
              ...edge.markerEnd,
              color: '#8b5cf6',
              width: 25,
              height: 25
            },
            className: 'column-edge-highlighted'
          };
        }
      }
      return edge;
    });
    return merged;
  }, [edges, columnLineageEdges, highlightedColumn]);

  // Loading component
  const LoadingComponent = () => (
    <div className="loading-container">
      {/* <div className="loading-spinner"></div> */}
      <img src={loader} alt="Loading..." className="h-30" />
      <div className="text-gray-700 text-[20px] mt-[15px] text-center">Loading lineage data...</div>
    </div>
  );

  // Error component
  const ErrorComponent = () => (
    <div className="error-container">
      <div className="error-icon">⚠️</div>
      <div className="error-title">Failed to load data</div>
      <div className="error-message">
        API Error: {error}
        <br />
        {/* <span className="fallback-notice">
          Using fallback data from DummyData.js
        </span> */}
      </div>
      <button className="retry-button" onClick={fetchLineageData}>
        Retry API Call
      </button>
    </div>
  );

  // Show loading state
  if (loading) {
    return (
      <div className="databricks-lineage-container">
        <LoadingComponent />
      </div>
    );
  }

  // Show error state (but still render the graph with fallback data)
  if (error) {
    return (
      <div className="databricks-lineage-container">
        <div className="error-banner">
          <ErrorComponent />
        </div>
        
      </div>
    );
  }

  return (
    <ReactFlowProvider>
      <div style={{ width: '100%', height: '100%', position: 'relative' }}>
        {/* Add Table and Delete Buttons */}
        <div className="viewlineage-buttons-container">
          <button 
            className="viewlineage-add-table-btn"
            onClick={handleAddTable}
          >
            + Add Table
          </button>
          <button 
            className="viewlineage-delete-btn"
            onClick={handleDeleteNode}
            disabled={!selectedNode}
          >
            Delete
          </button>
        </div>

        <ReactFlow
        nodes={nodesWithSelection}
        edges={allEdges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onNodeClick={onNodeClick}
        onPaneClick={onPaneClick}
        nodeTypes={nodeTypes}
        fitView
        className="lineage-flow"
        fitViewOptions={{ padding: 0.1 }}
        defaultViewport={{ x: 0, y: 0, zoom: 1 }}
        minZoom={0.1}
        maxZoom={4}
        >
          <Controls
            position="bottom-right" // or "top-left", "top-right", "bottom-left"
            showZoom={true}
            showFitView={true}
            showInteractive={true}
          />
          <MiniMap
            position="bottom-left" // or other positions
          />
          <Background
            variant={BackgroundVariant.Dots}
            gap={20}
            size={1}
            color="hsl(220 17% 17%)"
          />
        </ReactFlow>

        {/* Table Details Modal */}
        <TableDetailsModal
          isOpen={isModalOpen}
          onClose={handleModalClose}
          onSubmit={handleModalSubmit}
          editingNode={editingNode}
          nodes={nodes}
        />
      </div>
    </ReactFlowProvider>
  );
};

export default ViewLineage;





